import { isAxiosError } from 'axios';

// Конверт ответов сервисов: даже HTTP 200 может нести логическую ошибку (`success: false`).
export interface ApiEnvelope<T = unknown> {
  readonly success?: boolean;
  readonly error?: string;
  readonly error_description?: string;
  readonly data?: T;
  /** Что именно уже занято при ответе 409 (`ConflictResponse`): например, `inn` или `email`. */
  readonly conflictReason?: string;
}

// ── Таксономия ошибок транспорта ───────────────────────────────────────────────
// Транспорт нормализует низкоуровневые ошибки axios в эти типы, чтобы сервисы и
// экраны не разбирали AxiosError напрямую.

/** Нет ответа от сервера (обрыв сети, DNS, CORS-preflight и т.п.). Повтор безопасен для идемпотентных. */
export class NetworkError extends Error {
  override readonly cause?: unknown;
  constructor(message = 'Network error', cause?: unknown) {
    super(message);
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/** Истёк таймаут запроса. Для идемпотентных операций повтор безопасен. */
export class TimeoutError extends Error {
  constructor(message = 'Request timed out') {
    super(message);
    this.name = 'TimeoutError';
  }
}

/** HTTP-ответ с не-2xx статусом. Несёт статус и конверт ошибки, если он был. */
export class ApiError extends Error {
  readonly status: number;
  readonly envelope?: ApiEnvelope;
  /** Подсказка из заголовка Retry-After (мс), если сервер прислал. */
  readonly retryAfterMs?: number;
  constructor(
    status: number,
    message: string,
    options: { envelope?: ApiEnvelope; retryAfterMs?: number } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.envelope = options.envelope;
    this.retryAfterMs = options.retryAfterMs;
  }
}

/** Сессия мертва: refresh-токен истёк/невалиден. Экраны → на вход. */
export class AuthExpiredError extends Error {
  constructor(message = 'Session expired') {
    super(message);
    this.name = 'AuthExpiredError';
  }
}

/** Логическая ошибка при 2xx (`success:false` или отсутствие ожидаемых данных). */
export class ApiRequestError extends Error {
  readonly status?: number;
  readonly code?: string;
  readonly description?: string;
  constructor(
    message: string,
    options: { status?: number; code?: string; description?: string } = {},
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = options.status;
    this.code = options.code;
    this.description = options.description;
  }
}

// ── Хелперы для экранов ─────────────────────────────────────────────────────────

/** HTTP-статус из любой нормализованной ошибки (или ещё «сырого» AxiosError). */
export function getStatus(error: unknown): number | undefined {
  if (error instanceof ApiError) {
    return error.status;
  }
  if (error instanceof ApiRequestError) {
    return error.status;
  }
  if (isAxiosError(error)) {
    return error.response?.status;
  }
  return undefined;
}

/**
 * true для 502/503/504 — шлюз без живого апстрима / перегрузка / таймаут шлюза. Запрос не
 * обработан приложением (его обработчик такие коды не возвращает), поэтому это «сервис временно
 * недоступен», а не ошибка данных. Экраны показывают понятное сообщение с предложением повторить.
 */
export function isServiceUnavailable(error: unknown): boolean {
  const status = getStatus(error);
  return status === 502 || status === 503 || status === 504;
}

/** true для сетевых ошибок/таймаутов (ответа от сервера нет). */
export function isNetworkError(error: unknown): boolean {
  return (
    error instanceof NetworkError ||
    error instanceof TimeoutError ||
    (isAxiosError(error) && !error.response)
  );
}

/**
 * Код ошибки из конверта ответа (`error`), если бэкенд его прислал. Нужен там, где один HTTP-статус
 * несёт разные деловые смыслы: например, `trucking-service` намеренно отвечает 502 и на «заказ не ушёл
 * на сервер перевозчика» (заявка при этом уже закреплена), и на «смежный сервис недоступен». По одному
 * статусу их не различить, а показывать надо разное.
 */
export function getApiCode(error: unknown): string | undefined {
  if (error instanceof ApiError) {
    return error.envelope?.error;
  }
  if (error instanceof ApiRequestError) {
    return error.code;
  }
  if (isAxiosError(error)) {
    const payload = error.response?.data as ApiEnvelope | string | undefined;
    return typeof payload === 'string' ? undefined : payload?.error;
  }
  return undefined;
}

/** Что именно уже занято при ответе 409; undefined, если сервер причину не назвал. */
export function getConflictReason(error: unknown): string | undefined {
  return error instanceof ApiError ? error.envelope?.conflictReason : undefined;
}

/**
 * Сообщение сервера, которое можно показать пользователю. В отличие от {@link getApiMessage}, не возвращает коды и
 * технические строки вроде «Request failed with status 503»: подходит только текст на русском.
 */
export function getReadableApiMessage(error: unknown): string | undefined {
  // Ответ 5xx — сбой, а не ответ пользователю: текст ошибки базы бывает и на русском.
  if ((getStatus(error) ?? 0) >= 500) {
    return undefined;
  }
  const message = getApiMessage(error);
  return message && /[а-яё]/i.test(message) ? message : undefined;
}

/** Человекочитаемое сообщение об ошибке из конверта, если бэкенд его прислал. */
export function getApiMessage(error: unknown): string | undefined {
  if (error instanceof ApiError) {
    return error.envelope?.error_description ?? error.envelope?.error ?? error.message;
  }
  if (error instanceof ApiRequestError) {
    return error.description ?? error.code ?? error.message;
  }
  if (isAxiosError(error)) {
    const payload = error.response?.data as ApiEnvelope | string | undefined;
    if (typeof payload === 'string') {
      return payload || error.message;
    }
    return payload?.error_description ?? payload?.error ?? error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return undefined;
}
